/**
 * CompleteSetupChecklist — 5-item onboarding checklist for Dashboard
 *
 * Renders below CombinedAccountsWidget. Disappears at 5/5 complete.
 * No coaching language. Items link to relevant screens.
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useVerdicts } from '@/hooks/useVerdicts';
import { useSubscription } from '@/hooks/useSubscription';
import { T } from '@/styles/tokens';

interface ChecklistItem {
  label: string;
  done: boolean;
  route: string;
}

const CheckIcon = (
  <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
    <path d="M2 7l3.5 3.5L12 4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke={T.textMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity={0.4}>
    <path d="M9 18l6-6-6-6" />
  </svg>
);

export function CompleteSetupChecklist() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { verdicts } = useVerdicts();
  const { isPremium } = useSubscription();

  const items: ChecklistItem[] = useMemo(() => [
    {
      label: 'Price your first piece',
      done: verdicts.length >= 1,
      route: '/app/calculate',
    },
    {
      label: 'Record a sale',
      done: verdicts.some(v => v.sold === true),
      route: '/app/portfolio',
    },
    {
      label: 'Upgrade to Premium',
      done: isPremium === true,
      route: '/app/premium',
    },
    {
      label: 'Set a highest sale',
      done: profile?.highestSale != null,
      route: '/app/insights',
    },
    {
      label: 'Add a brand name',
      done: profile?.pdfBrandName != null && profile.pdfBrandName !== '',
      route: '/app/insights',
    },
  ], [verdicts, isPremium, profile]);

  const completedCount = items.filter(i => i.done).length;

  // Hide when all complete
  if (completedCount >= 5) return null;

  return (
    <div style={{ marginTop: 12 }}>
      {/* Header row */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 6,
      }}>
        <span style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '1.2px',
          color: T.textMuted,
          textTransform: 'uppercase' as const,
          fontFamily: T.fontFamily,
        }}>
          Complete your studio
        </span>
        <span style={{
          fontSize: 12,
          fontWeight: 600,
          color: T.primary,
          fontFamily: T.fontFamily,
        }}>
          {completedCount}/5
        </span>
      </div>

      {/* Progress bar */}
      <div style={{
        height: 4,
        borderRadius: 2,
        background: T.borderLight,
        overflow: 'hidden',
        marginBottom: 8,
      }}>
        <div style={{
          height: '100%',
          width: `${(completedCount / 5) * 100}%`,
          borderRadius: 2,
          background: T.primary,
          transition: 'width 350ms cubic-bezier(0.4, 0, 0.2, 1)',
        }} />
      </div>

      {/* Checklist card */}
      <div style={{
        background: T.white,
        borderRadius: T.radiusMd,
        overflow: 'hidden',
        boxShadow: T.shadowRaised,
      }}>
        {items.map((item, i) => (
          <div key={item.label}>
            {i > 0 && (
              <div style={{ height: 1, background: T.borderLight, margin: '0 16px' }} />
            )}
            <div
              onClick={() => { if (!item.done) navigate(item.route); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '14px 16px',
                gap: 12,
                cursor: item.done ? 'default' : 'pointer',
                opacity: item.done ? 0.5 : 1,
                minHeight: 44,
              }}
            >
              {/* Circle */}
              <div style={{
                width: 20,
                height: 20,
                borderRadius: 10,
                background: item.done ? T.primary : 'transparent',
                border: item.done ? 'none' : `1.5px solid ${T.gray300 || '#D1D5DB'}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
                {item.done && CheckIcon}
              </div>

              {/* Label */}
              <span style={{
                flex: 1,
                fontSize: 14,
                fontWeight: 500,
                color: T.text,
                fontFamily: T.fontFamily,
                textDecoration: item.done ? 'line-through' : 'none',
              }}>
                {item.label}
              </span>

              {/* Chevron (incomplete only) */}
              {!item.done && ChevronIcon}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
